/**
 * KYC Checklist Panel — dual sign-off (rep + upline) so applications go out with no missing docs.
 * Embedded in the Application Wizard's last step, and reusable in the KYC Queue.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import { CheckCircle2, XCircle, Clock, Upload, AlertTriangle, ListPlus } from 'lucide-react';
import useAuthStore from '../../lib/auth-store';

const STATUS_META = {
  missing: { label: 'Missing', color: 'bg-slate-100 text-slate-600 border-slate-300', icon: Clock },
  rep_submitted: { label: 'Pending Upline Review', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
};

export function KYCChecklistPanel({ leadId, applicationId, onReady, onChange }) {
  const { user } = useAuthStore();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectingSlot, setRejectingSlot] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [todoDate, setTodoDate] = useState('');

  const load = async () => {
    try {
      const d = applicationId
        ? await api.getKycChecklistForApplication(applicationId)
        : await api.getKycChecklistForLead(leadId);
      setChecklist(d);
      onReady?.(d.overall_status === 'ready_to_submit');
    } catch (e) {
      toast.error('Failed to load KYC checklist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [leadId, applicationId]);

  const isRep = checklist && user?.id === checklist.assigned_rep_id;
  const isUpline = checklist && (user?.id === checklist.upline_id || ['iso', 'super_admin'].includes(user?.role));

  const submitItem = async (slot) => {
    try {
      await api.submitKycItem(checklist.id, slot, {});
      toast.success('Marked submitted — sent to your upline for review');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to submit item');
    }
  };

  const approveItem = async (slot) => {
    try {
      await api.approveKycItem(checklist.id, slot);
      toast.success('Approved');
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Failed to approve item');
    }
  };

  const rejectItem = async () => {
    try {
      await api.rejectKycItem(checklist.id, rejectingSlot, rejectReason || 'Document unclear or invalid');
      toast.success('Rejected — a fix-it task was created for the rep');
      setRejectingSlot(null);
      setRejectReason('');
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Failed to reject item');
    }
  };

  const addMissingToTodo = async () => {
    if (!todoDate) { toast.error('Pick a date first'); return; }
    try {
      const res = await api.addKycMissingToTodo(checklist.id, { due_date: todoDate, due_time: '09:00' });
      toast.success(`Added ${res.tasks_created} missing item(s) to your to-do list`);
    } catch (e) {
      toast.error('Failed to add to to-do list');
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground" data-testid="kyc-loading">Loading KYC checklist…</p>;
  if (!checklist) return null;

  const missingCount = checklist.items.filter((i) => i.required && i.status !== 'approved').length;

  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="kyc-checklist-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold">KYC / Compliance Checklist</h3>
        <Badge
          variant="outline"
          className={checklist.overall_status === 'ready_to_submit' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-amber-100 text-amber-700 border-amber-300'}
          data-testid="kyc-overall-status-badge"
        >
          {checklist.overall_status === 'ready_to_submit' ? 'Ready to Submit' : checklist.overall_status === 'pending_review' ? 'Pending Upline Review' : 'Incomplete'}
        </Badge>
      </div>

      {checklist.overall_status !== 'ready_to_submit' && (
        <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2" data-testid="kyc-block-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          This application cannot be submitted until every required document is uploaded and approved by your upline.
        </div>
      )}

      <div className="space-y-2">
        {checklist.items.map((item) => {
          const meta = STATUS_META[item.status];
          const Icon = meta.icon;
          return (
            <div key={item.slot} className="flex items-center justify-between gap-2 rounded-lg border border-border/60 p-2.5" data-testid={`kyc-item-${item.slot}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  {item.label} {!item.required && <span className="text-[10px] text-muted-foreground">(optional)</span>}
                </p>
                {item.status === 'rejected' && item.rejection_reason && (
                  <p className="text-xs text-red-500 mt-0.5">Rejected: {item.rejection_reason}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`${meta.color} text-[10px]`}><Icon className="h-3 w-3 mr-1" />{meta.label}</Badge>
                {isRep && (item.status === 'missing' || item.status === 'rejected') && (
                  <Button size="sm" variant="outline" onClick={() => submitItem(item.slot)} data-testid={`kyc-submit-${item.slot}`}>
                    <Upload className="h-3 w-3 mr-1" /> Mark Submitted
                  </Button>
                )}
                {isUpline && item.status === 'rep_submitted' && (
                  <>
                    <Button size="sm" onClick={() => approveItem(item.slot)} data-testid={`kyc-approve-${item.slot}`}>Approve</Button>
                    <Button size="sm" variant="outline" className="text-red-500" onClick={() => setRejectingSlot(item.slot)} data-testid={`kyc-reject-${item.slot}`}>Reject</Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rejectingSlot && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2" data-testid="kyc-reject-form">
          <Textarea placeholder="Reason for rejection" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="kyc-reject-reason-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setRejectingSlot(null)} data-testid="kyc-reject-cancel-btn">Cancel</Button>
            <Button size="sm" className="bg-red-500 hover:bg-red-600" onClick={rejectItem} data-testid="kyc-reject-confirm-btn">Confirm Reject</Button>
          </div>
        </div>
      )}

      {missingCount > 0 && (
        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <Input type="date" value={todoDate} onChange={(e) => setTodoDate(e.target.value)} className="max-w-[160px]" data-testid="kyc-todo-date-input" />
          <Button size="sm" variant="outline" onClick={addMissingToTodo} data-testid="kyc-add-missing-todo-btn">
            <ListPlus className="h-3.5 w-3.5 mr-1.5" /> Add {missingCount} missing item(s) to my to-do list
          </Button>
        </div>
      )}
    </div>
  );
}
